import { Box, Typography, useTheme } from "@mui/material";
import {
  ArrowForward,
  LocalShipping,
  Inventory,
  ViewModule,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { z } from "zod";
import { usePageUrls } from "../../../hooks/usePageUrls";
import { RoutesIcon } from "../icons/RoutesIcon";
import TrailerIcon from "../icons/TrailerIcon";
import { DASH_PLACEHOLDER } from "../../../constants/dashPlaceholder";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { laneTypeMap } from "../../../types/outbound/lane.type";
import { useGetLanes } from "../../../hooks/react-query/outbound/useLanes";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";

export const currentLocationSchema = z.object({
  type: z.string(),
  name: z.string(),
  trailer: z.string().nullish(),
  tripNumber: z.number().nullish(),
  trapId: z.number().nullish(),
  pullId: z.number().nullish(),
});

type CurrentLocation = z.infer<typeof currentLocationSchema>;

interface ShipmentWithCurrentLocation {
  currentLocation?: CurrentLocation | null;
}

const getCurrentLocationValueGetter = <T extends ShipmentWithCurrentLocation>(
  params: GridRenderCellParams<T>
) => {
  const loc = params.row.currentLocation;
  if (!loc) return DASH_PLACEHOLDER;

  if (loc.type === "OnDock") {
    return DASH_PLACEHOLDER;
  } else if (
    loc.type !== "Trap" &&
    loc.type !== "Route" &&
    loc.pullId &&
    !loc.tripNumber
  ) {
    return DASH_PLACEHOLDER;
  }

  return loc.trailer || DASH_PLACEHOLDER;
};

const CurrentLocationCellOld = <T extends ShipmentWithCurrentLocation>({
  params,
}: {
  params: GridRenderCellParams<T>;
}) => {
  const theme = useTheme();
  const pageUrls = usePageUrls();
  const [serviceCenter] = useSelectedServiceCenter();
  const { data: lanes = [] } = useGetLanes(serviceCenter.code);
  const loc = params.row.currentLocation;

  if (!loc) return DASH_PLACEHOLDER;

  let icon = null;
  let link = null;
  let name = loc.name;
  let trailerText = loc.trailer || DASH_PLACEHOLDER;

  if (loc.type === "OnDock") {
    icon = <Inventory sx={{ color: theme.palette.grey[600], fontSize: 20 }} />;
    link = pageUrls.outboundTrailers("freightOnDock");
    name = loc.name;
    trailerText = DASH_PLACEHOLDER;
  } else if (loc.type === "Lane") {
    icon = (
      <ArrowForward sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
    );
    const lane = lanes.find((l) => l.destinationSic === loc.name);
    link = lane
      ? pageUrls.outboundShipmentDetails(
          lane.laneId,
          lane.destinationSic,
          laneTypeMap[lane.laneType]
        )
      : null;
  } else if (loc.type === "Route") {
    icon = <RoutesIcon />;
    link = loc.tripNumber
      ? pageUrls.outboundTrailerShipments({
          view: "pndTrailers",
          tripNumber: loc.tripNumber,
        })
      : null;
  } else if (loc.type === "Trap" && loc.trapId) {
    icon = <ViewModule sx={{ color: theme.palette.grey[600], fontSize: 20 }} />;
    link = pageUrls.outboundTrailerShipments({
      view: "traps",
      trapId: loc.trapId,
    });
  } else if (loc.type === "Trip" && loc.tripNumber) {
    icon = (
      <LocalShipping sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
    );
    link = pageUrls.outboundTrailerShipments({
      view: "linehaulTrailers",
      tripNumber: loc.tripNumber,
    });
  } else if (loc.pullId && loc.tripNumber) {
    icon = <TrailerIcon fillColor={theme.palette.grey[600]} size={20} />;
    link = pageUrls.outboundTrailerShipments({
      view: "outboundLoads",
      plannedPullId: loc.pullId,
    });
  } else if (loc.pullId && !loc.tripNumber) {
    icon = <TrailerIcon fillColor={theme.palette.grey[600]} size={20} />;
    link = pageUrls.outboundTrailerShipments({
      view: "outboundLoads",
      plannedPullId: loc.pullId,
    });
    trailerText = DASH_PLACEHOLDER;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={0.5}
    >
      <Box display="flex" alignItems="center" gap={0.5}>
        {icon}
        {link ? (
          <Link
            to={link}
            style={{
              textDecoration: "underline",
              color: theme.palette.primary.main,
              fontWeight: 500,
            }}
          >
            {name}
          </Link>
        ) : (
          <Typography
            component="span"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
            }}
          >
            {name}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" color={theme.palette.grey[700]}>
        {trailerText}
      </Typography>
    </Box>
  );
};

const CurrentLocationCell = <T extends ShipmentWithCurrentLocation>({
  params,
}: {
  params: GridRenderCellParams<T>;
}) => {
  const theme = useTheme();
  const pageUrls = usePageUrls();
  const [serviceCenter] = useSelectedServiceCenter();
  const { data: lanes = [] } = useGetLanes(serviceCenter.code);
  const loc = params.row.currentLocation;

  if (!loc) return DASH_PLACEHOLDER;

  let icon = null;
  let link = null;
  let name = loc.name;
  let trailerText = loc.trailer || DASH_PLACEHOLDER;

  if (loc.type === "OnDock") {
    icon = <Inventory sx={{ color: theme.palette.grey[600], fontSize: 20 }} />;
    link = pageUrls.outboundTrailers("freightOnDock");
    name = loc.name;
    trailerText = DASH_PLACEHOLDER;
  } else if (loc.type === "Lane") {
    icon = (
      <ArrowForward sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
    );
    const lane = lanes.find((l) => l.destinationSic === loc.name);
    link =
      lane &&
      pageUrls.outboundShipmentDetails(
        lane.laneId,
        lane.destinationSic,
        laneTypeMap[lane.laneType]
      );
  } else if (loc.type === "Route") {
    icon = <RoutesIcon />;
    link =
      loc.tripNumber &&
      pageUrls.outboundTrailerShipments({
        view: "pndTrailers",
        tripNumber: loc.tripNumber,
      });
  } else if (loc.type === "Trap") {
    icon = <ViewModule sx={{ color: theme.palette.grey[600], fontSize: 20 }} />;
    link =
      loc.trapId &&
      pageUrls.outboundTrailerShipments({
        view: "traps",
        trapId: loc.trapId,
        tripNumber: loc.tripNumber,
        plannedPullId: loc.pullId,
      });
  } else if (loc.type === "Trip") {
    icon = (
      <LocalShipping sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
    );
    link =
      loc.tripNumber &&
      pageUrls.outboundTrailerShipments({
        view: "linehaulTrailers",
        tripNumber: loc.tripNumber,
        plannedPullId: loc.pullId,
      });
  } else if (loc.type === "OutboundTrip") {
    icon = (
      <LocalShipping sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
    );
    link =
      loc.pullId &&
      pageUrls.outboundTrailerShipments({
        view: "outboundLoads",
        tripNumber: loc.tripNumber,
        plannedPullId: loc.pullId,
      });
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={0.5}
    >
      <Box display="flex" alignItems="center" gap={0.5}>
        {icon}
        {link ? (
          <Link
            to={link}
            style={{
              textDecoration: "underline",
              color: theme.palette.primary.main,
              fontWeight: 500,
            }}
          >
            {name}
          </Link>
        ) : (
          <Typography
            component="span"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
            }}
          >
            {name}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" color={theme.palette.grey[700]}>
        {trailerText}
      </Typography>
    </Box>
  );
};

export const useCurrentLocationColumn = <
  T extends ShipmentWithCurrentLocation,
>() => {
  const isCurrentLocEnabled = useIsFeatureFlagEnabled(
    "outbound-OD-1514-show-current-location-column-client"
  );

  const isOutboundTripEnabled = useIsFeatureFlagEnabled(
    "outbound-OD-1602-use-current-location-outbound-trip-client"
  );

  if (!isCurrentLocEnabled) {
    return [];
  }

  return [
    {
      field: "currentLocation",
      headerName: "Current Loc",
      minWidth: 140,
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: getCurrentLocationValueGetter<T>,
      renderCell: (params: GridRenderCellParams<T>) =>
        isOutboundTripEnabled ? (
          <CurrentLocationCell params={params} />
        ) : (
          <CurrentLocationCellOld params={params} />
        ),
    },
  ];
};
